import React, {useEffect, useState} from "react";
import {Button, Modal} from "antd";
import NewFlowForm from "../../forms/NewFlowForm";
import "./new-flow-modal.css";
import NewFlowStep2 from "./NewFlowStep2";
import NewFlowBuildTemplates from "./NewFlowBuildTemplates";
import Celebration from "../../../assets/illustrations/celebration.png";
import {getPossibleMessageTypesByTrigger} from "../../../utils/getPossibleMessageTypesByTrigger";
import {FlowTriggerProps} from "../../../types/FlowTriggerProps";
import useAppContext from "../../../hooks/useAppContext";

interface NewFlowModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export interface FormValuesProps {
    title: string;
    flowTypeId: number | null;
    flowTriggerId: number | null;
    distribution?: number;
    active: boolean;
}

export const DEFAULT_FORM_VALUES = {
    title: '', flowTypeId: null, flowTriggerId: null, distribution: 100, active: true
}

const NewFlowModal = ({
    isOpen,
    onClose,
}: NewFlowModalProps) => {
    const [flowId, setFlowId] = useState<number | null>(null);
    const [templateBuildingWay, setTemplateBuildingWay] = useState<number | string | null>(null);
    const [step, setStep] = useState(1);
    const [templatesCount, setTemplatesCount] = useState(0);
    const [flowTriggerId, setFlowTriggerId] = useState<number | null>(null);
    const { data: { flowTriggers, flowTypes }} = useAppContext();

    const flowTrigger = flowTriggers.data.find((trigger: FlowTriggerProps) => trigger.id === flowTriggerId);
    const possibleMessageTypes = getPossibleMessageTypesByTrigger(flowTrigger, flowTypes.data);
    const isMultipleTypes = possibleMessageTypes?.length > 1;
    const hasPresets = (flowTriggerId: number) => {
        return flowTriggerId === 1;
    }

    const onFlowCreation = (flowId: number, flowTriggerId: number) => {
        setFlowId(flowId);
        setFlowTriggerId(flowTriggerId);
        if(hasPresets(flowTriggerId)) {
            setStep(2);
        } else {
            setTemplateBuildingWay(1);
            setStep(3);
        }
    }

    const onTemplateBuildingWaySelect = (wayId: number | string) => {
        setTemplateBuildingWay(wayId);
        setStep(3);
    }

    const getWidthByStep = () => {
        if(step === 1 || step === 2) return 720;
        if(step === 3) return 1024;
        if(step === 4) return 750;
    }

    const onTemplatesCreated = () => {
        setTemplatesCount(templatesCount + 1);
        if(templatesCount + 1 < 5) {
            setStep(2);
        } else {
            setStep(4);
        }
    }

    const onCompleteFlow = () => {
        onClose();
    }

    useEffect(() => {
        if(!isOpen) {
            setStep(1);
            setFlowId(null);
            setTemplateBuildingWay(null);
            setTemplatesCount(0);
        }
    }, [isOpen]);

    return (
        <Modal
            open={isOpen}
            okButtonProps={{ style: { display: 'none' }}}
            cancelButtonProps={{ style: { display: 'none' }}}
            width={getWidthByStep()}
            destroyOnHidden={true}
            onCancel={onClose}
            className="new-flow-modal-container"
        >
            {step === 1 ? <>
                <div className="step-title">Create New Automation</div>
                <NewFlowForm onFlowCreation={onFlowCreation} onClose={onClose} />
            </> : null}
            {step === 2 && flowId ? <NewFlowStep2 templatesCount={templatesCount} onTemplateBuildingWaySelect={onTemplateBuildingWaySelect} completeFlow={onCompleteFlow} /> : null}
            {step === 3 && templateBuildingWay && flowId && flowTriggerId ? <NewFlowBuildTemplates flowTriggerId={flowTriggerId} flowId={flowId} templateBuildingWayId={templateBuildingWay as number} onTemplatesCreated={onTemplatesCreated} completeFlow={onCompleteFlow} onCancel={() => setStep(2)} /> : null}
            {step === 4 && <div className="step-container step-4">
                <div className="celebration-text">
                    Great job, you've successfully created a new Automation!
                </div>
                <img src={Celebration} className="celebration-image" />
                <Button type="primary" size="large" className="done-button" onClick={onClose} >Return to your Automations</Button>
            </div>}
        </Modal>
    );
}

export default NewFlowModal;